import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import React, { forwardRef } from 'react';
import { ChevronRight } from 'react-feather';
import { BreadcrumbItem } from './BreadcrumbItem.js';
import defaultStyles from './styles/Breadcrumbs.module.scss.js';
import { jsx } from 'react/jsx-runtime';
import { Link } from '../link/Link.js';
import { omit } from '../../utils/omit.js';

const defaultItemRender = (route, routes) => {
  if (routes.indexOf(route) === routes.length - 1) {
    return /*#__PURE__*/jsx("span", {
      children: route.title
    });
  }
  return /*#__PURE__*/jsx(Link, {
    to: route.path,
    title: route.title,
    children: route.title
  });
};
const Breadcrumbs = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    theme,
    children,
    maxCount,
    separator = /*#__PURE__*/jsx(ChevronRight, {}),
    dataTestId,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const routes = props?.routes ?? [];
  const itemRender = 'itemRender' in props ? props.itemRender : defaultItemRender;
  const testProps = {
    'data-test-id': dataTestId
  };
  const Ellipses = /*#__PURE__*/jsx("span", {
    className: styles.ellipses,
    children: "..."
  });
  const Separator = /*#__PURE__*/jsx("span", {
    className: styles.separator,
    children: separator
  });
  const getValidChild = (itemToRender, delta, index) => {
    const SeparatorWithKey = /*#__PURE__*/React.cloneElement(Separator, {
      key: `${index}_separator`
    });

    // Show ellipses
    if (delta > 0) {
      if (index === 0) {
        return [itemToRender, SeparatorWithKey, Ellipses];
      }
      if (index > delta) {
        return [SeparatorWithKey, itemToRender];
      }
      return null;
    }
    return index === 0 ? [itemToRender] : [SeparatorWithKey, itemToRender];
  };
  const getItemsByRoute = () => {
    const paths = [];
    const delta = routes.length - maxCount;
    return routes.map((route, index) => {
      paths.push((route.path || '').replace(/^\//, ''));
      return getValidChild( /*#__PURE__*/jsx(BreadcrumbItem, {
        children: itemRender(route, routes, paths)
      }, route.path || route.title), delta, index);
    });
  };
  const getItemsByChildren = () => {
    const delta = React.Children.toArray(children).length - maxCount;
    return React.Children.map(children, (child, index) => {
      return child && getValidChild( /*#__PURE__*/React.cloneElement(child), delta, index);
    });
  };
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    className: styles.root,
    ...testProps,
    ...omit(rest, ['itemRender', 'routes']),
    children: routes && routes.length ? getItemsByRoute() : getItemsByChildren()
  });
});
Breadcrumbs.displayName = 'Breadcrumbs';

export { Breadcrumbs };
